import { useEffect, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
  LineChart, Line,
} from 'recharts';
import { Download } from 'lucide-react';
import api from '@/lib/api';
import { exportCsv } from '@/lib/csv';

const COLORS = ['#6366F1', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#EC4899', '#64748B'];

interface Props {
  dateFrom: string;
  dateTo: string;
  granularity: 'day' | 'week' | 'month';
}

export function LeadReports({ dateFrom, dateTo, granularity }: Props) {
  const [byStatus, setByStatus] = useState<any[]>([]);
  const [bySource, setBySource] = useState<any[]>([]);
  const [byAssignee, setByAssignee] = useState<any[]>([]);
  const [trend, setTrend] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = { dateFrom, dateTo };
    setLoading(true);
    Promise.all([
      api.get('/reports/leads/by-status', { params }),
      api.get('/reports/leads/by-source', { params }),
      api.get('/reports/leads/by-assignee', { params }),
      api.get('/reports/leads/trend', { params: { ...params, granularity } }),
    ])
      .then(([s, src, a, t]) => {
        setByStatus(s.data);
        setBySource(src.data);
        setByAssignee(a.data);
        setTrend(
          t.data.map((d: any) => ({
            ...d,
            period: new Date(d.period).toLocaleDateString('en-IN', {
              day: '2-digit',
              month: 'short',
              ...(granularity !== 'day' ? {} : {}),
            }),
          })),
        );
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [dateFrom, dateTo, granularity]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-[#6366F1] border-t-transparent" />
      </div>
    );
  }

  // ── CSV export helpers ──
  // Each chart exports its own slice — keeps the rows meaningful
  // (e.g. "New, 12" for status) instead of a mega-wide denormalized
  // table no one reads.
  const handleExport = () => {
    if (byStatus.length)
      exportCsv('leads_by_status', byStatus, [{ key: 'name', label: 'Status' }, { key: 'value', label: 'Count' }]);
    if (bySource.length)
      exportCsv('leads_by_source', bySource, [{ key: 'name', label: 'Source' }, { key: 'value', label: 'Count' }]);
    if (byAssignee.length)
      exportCsv('leads_by_assignee', byAssignee, [{ key: 'name', label: 'Assignee' }, { key: 'value', label: 'Count' }]);
    if (trend.length)
      exportCsv('leads_trend', trend, [{ key: 'period', label: 'Period' }, { key: 'count', label: 'Leads' }]);
  };

  const hasAnyData = byStatus.length + bySource.length + byAssignee.length + trend.length > 0;

  return (
    <div className="space-y-5">
      <div className="flex justify-end">
        <button
          onClick={handleExport}
          disabled={!hasAnyData}
          className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-200 bg-white text-slate-700 hover:border-[#6366F1] hover:text-[#6366F1] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Download className="h-3.5 w-3.5" />
          Export CSV
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      {/* Leads by Status */}
      <div className="bg-white rounded-xl p-5 shadow-sm">
        <h3 className="text-sm font-semibold text-[#1E293B] mb-4">Leads by Status</h3>
        {byStatus.length === 0 ? (
          <p className="text-sm text-slate-400 py-8 text-center">No data available</p>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={byStatus}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="value" fill="#6366F1" radius={[4, 4, 0, 0]} name="Leads" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Leads by Source */}
      <div className="bg-white rounded-xl p-5 shadow-sm">
        <h3 className="text-sm font-semibold text-[#1E293B] mb-4">Leads by Source</h3>
        {bySource.length === 0 ? (
          <p className="text-sm text-slate-400 py-8 text-center">No data available</p>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={bySource} cx="50%" cy="50%" outerRadius={100} dataKey="value" nameKey="name" label={({ name, percent }) => `${name} ${((percent ?? 0) * 100).toFixed(0)}%`}>
                {bySource.map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Leads by Assignee */}
      <div className="bg-white rounded-xl p-5 shadow-sm">
        <h3 className="text-sm font-semibold text-[#1E293B] mb-4">Leads by Assignee</h3>
        {byAssignee.length === 0 ? (
          <p className="text-sm text-slate-400 py-8 text-center">No data available</p>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={byAssignee} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
              <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="value" fill="#8B5CF6" radius={[0, 4, 4, 0]} name="Leads" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Lead Trend */}
      <div className="bg-white rounded-xl p-5 shadow-sm">
        <h3 className="text-sm font-semibold text-[#1E293B] mb-4">Lead Trend</h3>
        {trend.length === 0 ? (
          <p className="text-sm text-slate-400 py-8 text-center">No data available</p>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="period" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Line type="monotone" dataKey="count" stroke="#6366F1" strokeWidth={2} dot={{ fill: '#6366F1', r: 4 }} name="Leads" />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
      </div>
    </div>
  );
}
